import { FC } from 'react'
import { Box, Typography } from '@mui/material'
import { keyframes } from '@mui/material/styles'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const R = '16px'

interface CostTableColumn {
  label: string
  key: string
  color?: string
  format?: (v: number) => string
  bold?: boolean
  width?: number
}

interface CostTableProps {
  title: string
  columns: CostTableColumn[]
  rows: Array<Record<string, string | number | boolean | null | undefined>>
  delay: number
}

export const CostTable: FC<CostTableProps> = ({ title, columns, rows, delay }) => {
  const gridCols = columns.map((col, i) => (col.width ? `${col.width}px` : i === 0 ? '1fr' : '120px')).join(' ')
  return (
    <Box
      sx={{
        background: '#fff', borderRadius: R, border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden', mt: 3, animation: `${fadeIn} 0.5s ease-out ${delay}ms both`,
      }}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          px: 2.5,
          py: 1,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        {columns.map((col, ci) => (
          <Typography
            key={col.key}
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'rgba(0,0,0,0.4)',
              textAlign: ci > 0 ? 'right' : undefined,
            }}
          >
            {col.label}
          </Typography>
        ))}
      </Box>
      {rows.map((row, i) => (
        <Box
          key={String(row[columns[0].key])}
          sx={{
            'display': 'grid', 'gridTemplateColumns': gridCols, 'px': 2.5, 'py': 1.5,
            'borderBottom': i < rows.length - 1 ? '1px solid rgba(0,0,0,0.03)' : 'none',
            'transition': 'background 0.15s', '&:hover': { background: 'rgba(0,0,0,0.015)' },
          }}
        >
          {columns.map((col, ci) => (
            <Typography
              key={col.key}
              sx={{
                fontSize: 13,
                fontWeight: col.bold ? 700 : 600,
                color: ci === 0 ? 'rgba(0,0,0,0.85)' : (col.color ?? 'rgba(0,0,0,0.85)'),
                textAlign: ci > 0 ? 'right' : undefined,
                fontVariantNumeric: ci > 0 ? 'tabular-nums' : undefined,
                overflow: ci === 0 ? 'hidden' : undefined,
                textOverflow: ci === 0 ? 'ellipsis' : undefined,
                whiteSpace: ci === 0 ? 'nowrap' : undefined,
              }}
            >
              {ci === 0
                ? String(row[col.key] ?? '')
                : (col.format ?? String)(row[col.key] as number)}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  )
}
